import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { idMapToArray, ScopeService } from './scope.service';

export interface AssetTypeNomenclature extends KPI.AssetType {
  super_category: KPI.SuperCategory;
  category: KPI.Category;
  sub_category: KPI.SubCategory;
  zones: { [id: number]: boolean };
  investment_types: { [id: number]: boolean };
}

export interface LengthMap {
  [id: number]: number;
}

@Injectable()
export class NomenclatureService {
  public superCategoriesLengthMap$ = new BehaviorSubject<LengthMap>({});
  public categoriesLengthMap$ = new BehaviorSubject<LengthMap>({});
  public subCategoriesLengthMap$ = new BehaviorSubject<LengthMap>({});

  // Store the number of items of each super-category, category and subcategories
  // to merge the cells in the spreadsheet view
  private superCategoriesLengthMap: LengthMap = {};
  private categoriesLengthMap: LengthMap = {};
  private subCategoriesLengthMap: LengthMap = {};

  private _assetTypes$: Observable<AssetTypeNomenclature[]>;

  constructor(private api: APIService, private scope: ScopeService) { }

  public get assetTypes$(): Observable<AssetTypeNomenclature[]> {
    if (!this._assetTypes$) {
      this._assetTypes$ = this.getAssetTypes();
    }
    return this._assetTypes$;
  }

  public updateAssetType(
    id: number,
    assetType: Partial<KPI.AssetType>,
  ): Observable<KPI.AssetType> {
    return this.api
      .assets_asset_type_patch(id, assetType)
      .pipe(tap(() => this.scope.refreshAssetTypes()));
  }

  public updateInvestmentType(
    id: number,
    investmentType: Partial<KPI.InvestmentType>,
  ): Observable<KPI.InvestmentType> {
    return this.api
      .investments_investment_types_patch(id, investmentType)
      .pipe(tap(() => this.scope.refreshInvestmentTypes()));
  }

  private getAssetTypes(): Observable<AssetTypeNomenclature[]> {
    return combineLatest(
      this.scope.superCategories$,
      this.scope.categories$,
      this.scope.subCategories$,
      this.scope.assetTypes$.pipe(idMapToArray()),
      this.scope.zones$.pipe(idMapToArray()),
      this.scope.investmentTypes$.pipe(idMapToArray()),
    ).pipe(
      map(
        ([
          superCategories,
          categories,
          subCategories,
          assetTypes,
          zones,
          investmentTypes,
        ]) => {
          this.categoriesLengthMap = {};
          this.subCategoriesLengthMap = {};

          const assetTypesNomenclature: AssetTypeNomenclature[] =
            assetTypes.map((assetType: KPI.AssetType) => {
              const subCategory = subCategories[assetType.parent];
              const category = categories[subCategory.parent];
              const superCategory = superCategories[category.parent];

              this.subCategoriesLengthMap[subCategory.id] =
                (this.subCategoriesLengthMap[subCategory.id] || 0) + 1;
              this.categoriesLengthMap[category.id] =
                (this.categoriesLengthMap[category.id] || 0) + 1;
              this.superCategoriesLengthMap[superCategory.id] =
                (this.superCategoriesLengthMap[superCategory.id] || 0) + 1;

              return {
                ...assetType,
                super_category: superCategory,
                category,
                sub_category: subCategory,
                zones: zones.reduce(
                  (mapping, zone) => ({
                    ...mapping,
                    [zone.id]:
                      // If it's empty, it means the asset type is available everywhere
                      assetType.only_these_zones.length === 0 ||
                      R.includes(zone.id, assetType.only_these_zones),
                  }),
                  {},
                ),
                investment_types: investmentTypes.reduce(
                  (mapping, investmentType) => ({
                    ...mapping,
                    [investmentType.id]:
                      // If it's empty, it means the investment type is available for every type
                      investmentType.only_for_asset_types.length === 0 ||
                      R.includes(
                        assetType.id,
                        investmentType.only_for_asset_types,
                      ),
                  }),
                  {},
                ),
              };
            });
          const sortedAssetTypesNomenclature =
            R.sortWith<AssetTypeNomenclature>(
              [
                R.ascend(R.path(['super_category', 'ordering'])),
                R.ascend(R.path(['category', 'ordering'])),
                R.ascend(R.path(['sub_category', 'ordering'])),
                R.prop('ordering'),
              ],
              assetTypesNomenclature,
            );

          this.superCategoriesLengthMap$.next(this.superCategoriesLengthMap);
          this.categoriesLengthMap$.next(this.categoriesLengthMap);
          this.subCategoriesLengthMap$.next(this.subCategoriesLengthMap);

          return sortedAssetTypesNomenclature;
        },
      ),
      shareReplay(1),
    );
  }
}
